// Base Map Utility - Shared initialization logic for US maps
export class BaseMapUtil {
	static async initializeBaseMap(config) {
		const {
			containerId,
			svgId,
			errorPrefix = 'Map',
			enableZoom = false,
			onStateClick = null,
			getStateColor = () => '#f0f0f0',
			onStateHover = null,
			onStateMouseOut = null,
			afterStatesDrawn = null
		} = config;

		// Check dependencies
		if (!window.d3 || !window.topojson) {
			console.error('D3 or TopoJSON not loaded');
			return null;
		}

		// Get DOM elements
		const container = document.getElementById(containerId);
		const svg = document.getElementById(svgId);
		
		if (!container || !svg) {
			console.error(`${errorPrefix} container or SVG not found`);
			return null;
		}

		// Clear any existing content
		d3.select(svg).selectAll('*').remove();

		// Set up dimensions
		const width = container.clientWidth;
		const height = container.clientHeight;

		const svgSelection = d3.select(svg)
			.attr('width', width)
			.attr('height', height);

		const projection = d3.geoAlbersUsa();
		const path = d3.geoPath(projection);

		// Set up zoom behavior if enabled
		let zoom = null;
		let g = svgSelection.append('g');
		
		if (enableZoom) {
			zoom = d3.zoom()
				.scaleExtent([1, 8])
				.on('zoom', (event) => {
					const { transform } = event;
					g.attr('transform', transform);
					
					// Keep legend positioned on the right side during zoom
					const legendGroup = svgSelection.select('.state-legend');
					if (!legendGroup.empty()) {
						// Recalculate legend position to stay on right side
						const currentWidth = svgSelection.node().getBoundingClientRect().width;
						const newLegendX = currentWidth - 21;
						
						legendGroup.selectAll('.legend-item rect')
							.attr('x', newLegendX);
						legendGroup.selectAll('.legend-item text')
							.attr('x', newLegendX + 10); // 10 is half of boxSize (20)
					}
				});

			svgSelection.call(zoom);
		}
		try {
			// Load US map data
			const us = await d3.json('/wp-content/themes/careerreadyhq/vendor/states-10m.json');
			
			// Store states data
			const usStatesData = topojson.feature(us, us.objects.states);
		
			// Adjust projection for mobile to shift map left (less space for Alaska)
			const isMobile = width < 768;
			if (isMobile) {
				projection.fitSize([width, height], usStatesData);
				// Shift the map left on mobile
				const currentTranslate = projection.translate();
				projection.translate([currentTranslate[0] - 30, currentTranslate[1]]);
			} else {
				projection.fitSize([width, height], usStatesData);
			}
			
			// Draw states
			const states = g.append('g')
				.selectAll('path')
				.data(topojson.feature(us, us.objects.states).features)
				.join('path')
				.attr('d', path)
				.attr('fill', (d) => getStateColor(d.properties.name))
				.attr('stroke', '#000000')
				.attr('stroke-width', 1)
				.style('cursor', 'pointer')
				.style('transition', 'opacity 0.2s ease');

			// Add click handler if provided
			if (onStateClick) {
				states.on('click', (event, d) => {
					onStateClick(event, d);
				});
			}

			// Add hover handlers if provided
			if (onStateHover) {
				states.on('mouseover', (event, d) => {
					onStateHover(event, d);
				});
			}

			if (onStateMouseOut) {
				states.on('mouseout', (event, d) => {
					onStateMouseOut(event, d);
				});
			}

			// State name to abbreviation mapping
			const stateAbbreviations = {
				'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
				'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
				'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
				'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
				'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
				'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
				'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
				'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
				'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
				'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY',
				'District of Columbia': 'DC'
			};

			// States that should not have abbreviations on the map (will be in legend instead)
			const smallStates = [
				'Connecticut',
				'Massachusetts',
				'Delaware',
				'District of Columbia',
				'Vermont',
				'Maryland',
				'New Hampshire',
				'New Jersey',
				'Rhode Island'
			];

			// Add state abbreviation labels (excluding small states)
			const stateLabels = g.append('g')
				.selectAll('text')
				.data(topojson.feature(us, us.objects.states).features)
				.join('text')
				.attr('class', 'state-label')
				.attr('x', (d) => path.centroid(d)[0])
				.attr('y', (d) => path.centroid(d)[1])
				.attr('text-anchor', 'middle')
				.attr('dy', '0.35em')
				.attr('font-family', 'Arial, sans-serif')
				.attr('font-size', '11px')
				.attr('font-weight', 'bold')
				.attr('fill', '#333333')
				.attr('pointer-events', 'none')
				.text((d) => {
					// Only show abbreviation if state is not in smallStates list
					if (smallStates.includes(d.properties.name)) {
						return '';
					}
					return stateAbbreviations[d.properties.name] || '';
				})
				.style('opacity', 0.8); // Make state labels slightly transparent

			// Add state borders
			g.append('path')
				.datum(topojson.mesh(us, us.objects.states, (a, b) => a !== b))
				.attr('fill', 'none')
				.attr('stroke', '#000000')
				.attr('stroke-linejoin', 'round')
				.attr('d', path);

			// Create legend for small states
			const legendData = smallStates
				.map(stateName => ({
					name: stateName,
					abbr: stateAbbreviations[stateName]
				}))
				.sort((a, b) => a.abbr.localeCompare(b.abbr)); // Sort alphabetically by abbreviation

			// Position legend on the right side of the map
			const legendX = width-21;
			const legendStartY = (height - (legendData.length * 30)) / 2; // Center vertically
			const boxSize = 20;
			const boxSpacing = 30; // 20px box + 10px spacing

			const legendGroup = svgSelection.append('g')
				.attr('class', 'state-legend')
				.style('position', 'absolute')
				.style('z-index', '1000');

			// Create legend items
			const legendItems = legendGroup.selectAll('.legend-item')
				.data(legendData)
				.join('g')
				.attr('class', 'legend-item')
				.attr('data-state', d => d.name);

			// Add boxes for each state
			legendItems.append('rect')
				.attr('class', 'legend-box')
				.attr('x', legendX)
				.attr('y', (d, i) => legendStartY + (i * boxSpacing))
				.attr('width', boxSize)
				.attr('height', boxSize)
				.attr('fill', d => {
					// Find the state feature to get its color
					const stateFeature = topojson.feature(us, us.objects.states).features
						.find(f => f.properties.name === d.name);
					return stateFeature ? getStateColor(d.name) : '#f0f0f0';
				})
				.attr('stroke', '#000000')
				.attr('stroke-width', 1)
				.style('cursor', 'pointer')
				.style('transition', 'stroke-width 0.2s ease');

			// Add labels for each state
			legendItems.append('text')
				.attr('class', 'legend-label')
				.attr('x', legendX + (boxSize / 2))
				.attr('y', (d, i) => legendStartY + (i * boxSpacing) + (boxSize / 2))
				.attr('dy', '0.35em')
				.attr('text-anchor', 'middle')
				.attr('font-family', 'Arial, sans-serif')
				.attr('font-size', '12px')
				.attr('font-weight', 'bold')
				.attr('fill', '#333333')
				.text(d => d.abbr);

			// Add hover effect for legend boxes
			legendItems.on('mouseover', function(event, d) {
				// Increase border width of legend box
				d3.select(this).select('rect')
					.attr('stroke-width', 3);

				// Find and trigger hover on corresponding state
				const stateElement = states.filter((stateData) => 
					stateData.properties.name === d.name
				).node();

				if (stateElement && onStateHover) {
					const stateData = d3.select(stateElement).datum();
					onStateHover(event, stateData);
				}
			});

			legendItems.on('mouseout', function(event, d) {
				// Reset border width
				d3.select(this).select('rect')
					.attr('stroke-width', 1);

				// Find and trigger mouseout on corresponding state
				const stateElement = states.filter((stateData) => 
					stateData.properties.name === d.name
				).node();

				if (stateElement && onStateMouseOut) {
					const stateData = d3.select(stateElement).datum();
					onStateMouseOut(event, stateData);
				}
			});

			// Add touch support for legend boxes
			// legendItems.on('touchstart', function(event, d) {
			// 	event.preventDefault(); // Prevent default touch behavior
			// 	// Trigger mouseover behavior
			// 	d3.select(this).select('rect')
			// 		.attr('stroke-width', 3);
			// });

			// Add click handler for legend boxes (also handles touch via tap)
			legendItems.on('click', function(event, d) {
				// Find the corresponding state
				const stateElement = states.filter((stateData) => 
					stateData.properties.name === d.name
				).node();

				if (stateElement) {
					const stateData = d3.select(stateElement).datum();
					
					// Highlight the legend box
					legendItems.selectAll('rect').attr('stroke-width', 1);
					d3.select(this).select('rect').attr('stroke-width', 3);

					// Trigger the state click handler
					if (onStateClick) {
						onStateClick(event, stateData);
					}
				}
			});

			// Modify state hover handlers to also highlight legend
			states.on('mouseover', function(event, d) {
				// Check if this is a small state
				if (smallStates.includes(d.properties.name)) {
					// Find and highlight corresponding legend item
					const legendItem = legendItems.filter(legendData => 
						legendData.name === d.properties.name
					);
					legendItem.select('rect').attr('stroke-width', 3);
				}

				// Call original hover handler
				if (onStateHover) {
					onStateHover(event, d);
				}
			});

			states.on('mouseout', function(event, d) {
				// Check if this is a small state
				if (smallStates.includes(d.properties.name)) {
					// Reset legend item border
					const legendItem = legendItems.filter(legendData => 
						legendData.name === d.properties.name
					);
					legendItem.select('rect').attr('stroke-width', 1);
				}

				// Call original mouseout handler
				if (onStateMouseOut) {
					onStateMouseOut(event, d);
				}
			});

			// Override state click handler to also highlight legend
			states.on('click', function(event, d) {
				// Check if this is a small state
				if (smallStates.includes(d.properties.name)) {
					// Highlight corresponding legend item
					legendItems.selectAll('rect').attr('stroke-width', 1);
					const legendItem = legendItems.filter(legendData => 
						legendData.name === d.properties.name
					);
					legendItem.select('rect').attr('stroke-width', 3);
				}

				// Call original click handler
				if (onStateClick) {
					onStateClick(event, d);
				}
			});

			// Call post-drawing callback if provided
			if (afterStatesDrawn) {
				afterStatesDrawn({ svgSelection, zoom, states, width, height });
			}

			// Set up resize observer for responsive behavior (skip for components with zoom)
		let resizeObserver = null;
		let resizeTimeout = null;
		
		if (!enableZoom && container) {
			// Only add resize observer for non-zoomable maps (like heatmaps)
			const handleResize = (entries) => {
				const entry = entries[0];
				const newWidth = entry.contentRect.width;
				const newHeight = entry.contentRect.height;
				
				// Only resize if dimensions changed significantly (more than 10px)
				if (Math.abs(newWidth - width) < 10 && Math.abs(newHeight - height) < 10) {
					return;
				}
				
				if (resizeTimeout) clearTimeout(resizeTimeout);
				resizeTimeout = setTimeout(() => {
					// Temporarily disconnect observer
					if (resizeObserver) {
						resizeObserver.disconnect();
					}
					
					// Reinitialize the map with new dimensions
					BaseMapUtil.initializeBaseMap(config).then(() => {
						// Reconnect observer after reinitialization
						if (resizeObserver && container) {
							resizeObserver.observe(container);
						}
					});
				}, 250);
			};
			
			resizeObserver = new ResizeObserver(handleResize);
			resizeObserver.observe(container);
		}

			// Return map context with cleanup function
		return {
			svgSelection,
			zoom,
			pathGenerator: path,
			statesSelection: states,
			stateLabels,
			usStatesData,
			width,
			height,
			projection,
			legendItems,
				cleanup: () => {
					if (resizeObserver) {
						resizeObserver.disconnect();
						resizeObserver = null;
					}
					if (resizeTimeout) {
						clearTimeout(resizeTimeout);
						resizeTimeout = null;
					}
				}
			};

		} catch (error) {
			console.error(`Error loading ${errorPrefix.toLowerCase()} data:`, error);
			return null;
		}
	}
}